import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { 
  ArrowLeft, Bot, Sparkles, Upload, X, Camera, SwitchCamera, 
  Shirt, User, Ruler, Plus, Trash2, Loader2, Image as ImageIcon,
  Copy, Check, Eye, Volume2, Square, Mic, MicOff, Send, MessageCircle,
  Wand2, ZoomIn, Video, Film, Play, Share2, Hash, Users
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Voice options for TTS
const VOICE_OPTIONS = [
  { id: "nova", label: "Nova", description: "Energetic, upbeat" },
  { id: "alloy", label: "Alloy", description: "Neutral, balanced" },
  { id: "shimmer", label: "Shimmer", description: "Bright, cheerful" },
  { id: "echo", label: "Echo", description: "Smooth, calm" },
  { id: "onyx", label: "Onyx", description: "Deep, authoritative" },
  { id: "fable", label: "Fable", description: "Expressive, storytelling" }
];

// Analysis types configuration
const ANALYSIS_TYPES = [
  {
    id: "style",
    label: "Style Analysis",
    icon: Sparkles,
    description: "Analyze fashion style, outfit coordination, and recommendations",
    color: "from-purple-500 to-pink-500",
    prompt: "style analysis"
  },
  {
    id: "posture",
    label: "Body Posture",
    icon: User,
    description: "Analyze body posture for tailoring adjustments",
    color: "from-blue-500 to-cyan-500",
    prompt: "body posture analysis for tailoring"
  },
  {
    id: "garment",
    label: "Garment Analysis",
    icon: Shirt,
    description: "Analyze garment fit and suggest improvements",
    color: "from-amber-500 to-orange-500",
    prompt: "garment fit analysis and improvements"
  }
];

// Video analysis focus options
const VIDEO_FOCUS_OPTIONS = [
  { id: "general", label: "General Overview", description: "Comprehensive summary of all content" },
  { id: "style", label: "Style & Fashion", description: "Focus on fashion trends and styling" },
  { id: "posture", label: "Body Posture", description: "Analyze posture for tailoring" },
  { id: "garment", label: "Garment Analysis", description: "Focus on garment construction and fit" },
  { id: "technique", label: "Techniques", description: "Extract tailoring techniques and tips" }
];

const AdminAITailorPage = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [activeAnalysis, setActiveAnalysis] = useState("style");
  const [images, setImages] = useState([]);
  const [analysisResults, setAnalysisResults] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [additionalNotes, setAdditionalNotes] = useState("");
  const [copied, setCopied] = useState(false);
  
  // Voice and conversation state
  const [selectedVoice, setSelectedVoice] = useState("nova");
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isGeneratingAudio, setIsGeneratingAudio] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [isChatLoading, setIsChatLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [showChat, setShowChat] = useState(false);
  
  // Visualization state
  const [isGeneratingViz, setIsGeneratingViz] = useState(false);
  const [visualizationImage, setVisualizationImage] = useState(null);
  const [showVisualization, setShowVisualization] = useState(false);

  // Share to Messenger
  const [shareOpen, setShareOpen] = useState(false);
  const [shareContent, setShareContent] = useState("");
  const [shareNote, setShareNote] = useState("");
  
  // Video analysis state
  const [video, setVideo] = useState(null);
  const [videoPreview, setVideoPreview] = useState(null);
  const [isAnalyzingVideo, setIsAnalyzingVideo] = useState(false);
  const [videoAnalysisResult, setVideoAnalysisResult] = useState(null);
  const [videoAnalysisFocus, setVideoAnalysisFocus] = useState("general");
  const videoInputRef = useRef(null);
  
  // Voice recording state
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioRef = useRef(null);
  const chatEndRef = useRef(null);
  
  // Camera state
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  const [facingMode, setFacingMode] = useState("environment");
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    const adminAuth = sessionStorage.getItem("adminAuth");
    if (adminAuth !== "true") {
      navigate("/admin");
    } else {
      setIsAuthenticated(true);
    }
  }, [navigate]);

  // Generate unique session ID
  useEffect(() => {
    setSessionId(`chat_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  }, []);

  // Scroll to bottom of chat
  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [chatMessages]);

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  // Camera functions
  const startCamera = useCallback(async () => {
    setCameraError(null);
    try {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      const constraints = {
        video: {
          facingMode: facingMode,
          width: { ideal: 1280 },
          height: { ideal: 720 }
        }
      };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (err) {
      console.error("Camera error:", err);
      if (err.name === "NotAllowedError") {
        setCameraError("Camera access denied. Please allow camera access.");
      } else if (err.name === "NotFoundError") {
        setCameraError("No camera found on this device.");
      } else {
        setCameraError("Unable to access camera.");
      }
    }
  }, [facingMode]);

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  }, []);

  const openCamera = async () => {
    setIsCameraOpen(true);
    await startCamera();
  };

  const closeCamera = () => {
    stopCamera();
    setIsCameraOpen(false);
    setCameraError(null);
  };

  const switchCamera = async () => {
    setFacingMode(prev => prev === "user" ? "environment" : "user");
  };

  useEffect(() => {
    if (isCameraOpen && !cameraError) {
      startCamera();
    }
  }, [facingMode, isCameraOpen, cameraError, startCamera]);

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
    const base64 = dataUrl.split(',')[1];
    
    addImage({
      id: `img_${Date.now()}`,
      base64,
      preview: dataUrl,
      type: 'image/jpeg',
      name: `camera_${Date.now()}.jpg`
    });
    
    closeCamera();
  };

  useEffect(() => {
    return () => {
      stopCamera();
    };
  }, [stopCamera]);

  // Image handling
  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files);
    files.forEach(file => {
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name} is too large. Max 10MB.`);
        return;
      }
      if (!file.type.startsWith('image/')) {
        toast.error(`${file.name} is not an image.`);
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result.split(',')[1];
        addImage({
          id: `img_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          base64,
          preview: reader.result,
          type: file.type,
          name: file.name
        });
      };
      reader.readAsDataURL(file);
    });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const addImage = (imageData) => {
    setImages(prev => [...prev, imageData]);
    setAnalysisResults(null);
    setChatMessages([]);
    setShowChat(false);
  };

  const removeImage = (imageId) => {
    setImages(prev => prev.filter(img => img.id !== imageId));
    setAnalysisResults(null);
  };

  const clearAllImages = () => {
    setImages([]);
    setAnalysisResults(null);
    setChatMessages([]);
    setShowChat(false);
  };

  // Video handling functions
  const handleVideoUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    // Check file size (max 50MB for video)
    if (file.size > 50 * 1024 * 1024) {
      toast.error("Video must be less than 50MB");
      return;
    }
    
    // Check file type
    const validTypes = ['video/mp4', 'video/webm', 'video/quicktime', 'video/x-msvideo'];
    if (!validTypes.includes(file.type)) {
      toast.error("Please upload MP4, WebM, MOV, or AVI video");
      return;
    }
    
    // Create preview URL
    const previewUrl = URL.createObjectURL(file);
    setVideoPreview(previewUrl);
    
    // Convert to base64
    const reader = new FileReader();
    reader.onload = (e) => {
      const base64 = e.target.result.split(',')[1];
      setVideo({
        base64,
        type: file.type.split('/')[1],
        name: file.name,
        size: file.size
      });
      setVideoAnalysisResult(null);
    };
    reader.readAsDataURL(file);
    
    toast.success(`Video loaded: ${file.name}`);
  };

  const clearVideo = () => {
    if (videoPreview) {
      URL.revokeObjectURL(videoPreview);
    }
    setVideo(null);
    setVideoPreview(null);
    setVideoAnalysisResult(null);
    if (videoInputRef.current) {
      videoInputRef.current.value = '';
    }
  };

  const analyzeVideo = async () => {
    if (!video) {
      toast.error("Please upload a video first");
      return;
    }
    
    setIsAnalyzingVideo(true);
    
    try {
      const response = await axios.post(`${API}/admin/ai-tailor/analyze-video`, {
        video_base64: video.base64,
        video_type: video.type,
        video_name: video.name,
        analysis_focus: videoAnalysisFocus,
        additional_notes: additionalNotes
      });
      
      if (response.data.success) {
        setVideoAnalysisResult(response.data.analysis);
        toast.success("Video analysis complete!");
      }
    } catch (error) {
      console.error("Video analysis error:", error);
      toast.error(error.response?.data?.detail || "Video analysis failed");
    } finally {
      setIsAnalyzingVideo(false);
    }
  };

  // Text-to-Speech using OpenAI API
  const speakText = async (text) => {
    if (!text || isGeneratingAudio) return;
    
    // Stop current audio if playing
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
      setIsSpeaking(false);
    }
    
    setIsGeneratingAudio(true);
    
    try {
      const response = await axios.post(`${API}/admin/ai-tailor/tts`, {
        text: text,
        voice: selectedVoice,
        speed: 1.0
      });
      
      if (response.data.success && response.data.audio_base64) {
        const audio = new Audio(`data:audio/mp3;base64,${response.data.audio_base64}`);
        audioRef.current = audio;
        
        audio.onplay = () => setIsSpeaking(true);
        audio.onended = () => {
          setIsSpeaking(false);
          audioRef.current = null;
        };
        audio.onerror = () => {
          setIsSpeaking(false);
          audioRef.current = null;
          toast.error("Audio playback failed");
        };
        
        await audio.play();
      }
    } catch (error) {
      console.error("TTS error:", error);
      toast.error("Failed to generate speech");
    } finally {
      setIsGeneratingAudio(false);
    }
  };

  const stopSpeaking = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setIsSpeaking(false);
  };

  // Speech-to-Text using OpenAI Whisper
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach(track => track.stop());
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        await transcribeAudio(audioBlob);
      };
      
      mediaRecorder.start();
      setIsRecording(true);
      toast.info("Recording... Click again to stop");
    } catch (error) {
      console.error("Recording error:", error);
      toast.error("Failed to access microphone");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const transcribeAudio = async (audioBlob) => {
    setIsTranscribing(true);
    
    try {
      const reader = new FileReader();
      reader.readAsDataURL(audioBlob);
      
      reader.onloadend = async () => {
        const base64Audio = reader.result.split(',')[1];
        
        const response = await axios.post(`${API}/admin/ai-tailor/stt`, {
          audio_base64: base64Audio,
          format: 'webm'
        });
        
        if (response.data.success && response.data.text) {
          setChatInput(response.data.text);
          toast.success("Transcription complete");
        }
        setIsTranscribing(false);
      };
    } catch (error) {
      console.error("Transcription error:", error);
      toast.error("Failed to transcribe audio");
      setIsTranscribing(false);
    }
  };

  const toggleRecording = () => {
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  // Chat with AI
  const sendChatMessage = async (messageText = null) => {
    const message = messageText || chatInput.trim();
    if (!message || isChatLoading) return;
    
    setChatInput("");
    setIsChatLoading(true);
    
    // Add user message to chat
    const userMessage = {
      role: "user",
      content: message,
      timestamp: new Date().toISOString()
    };
    setChatMessages(prev => [...prev, userMessage]);
    
    try {
      const response = await axios.post(`${API}/admin/ai-tailor/chat`, {
        session_id: sessionId,
        message: message,
        analysis_context: analysisResults?.content || null
      });
      
      if (response.data.success) {
        const assistantMessage = {
          role: "assistant",
          content: response.data.response,
          timestamp: new Date().toISOString()
        };
        setChatMessages(prev => [...prev, assistantMessage]);
        
        // Auto-speak the response
        await speakText(response.data.response);
      }
    } catch (error) {
      console.error("Chat error:", error);
      toast.error("Failed to get response");
      setChatMessages(prev => prev.slice(0, -1)); // Remove the user message on error
    } finally {
      setIsChatLoading(false);
    }
  };

  // Generate adjustment visualization
  const generateVisualization = async () => {
    if (!analysisResults?.content) {
      toast.error("No analysis to visualize");
      return;
    }

    setIsGeneratingViz(true);
    setVisualizationImage(null);

    try {
      // Determine garment type from analysis
      let garmentType = "suit";
      const analysisLower = analysisResults.content.toLowerCase();
      if (analysisLower.includes("trouser") || analysisLower.includes("pant")) {
        garmentType = "trousers";
      } else if (analysisLower.includes("shirt")) {
        garmentType = "dress shirt";
      } else if (analysisLower.includes("vest")) {
        garmentType = "vest";
      } else if (analysisLower.includes("jacket") && !analysisLower.includes("suit")) {
        garmentType = "sport jacket";
      }

      const response = await axios.post(`${API}/admin/ai-tailor/visualize`, {
        analysis_text: analysisResults.content,
        garment_type: garmentType,
        original_image_base64: images.length > 0 ? images[0].base64 : null
      }, {
        timeout: 120000 // 2 minute timeout for image generation
      });

      if (response.data.success && response.data.image_base64) {
        setVisualizationImage({
          base64: response.data.image_base64,
          summary: response.data.prompt_summary
        });
        setShowVisualization(true);
        toast.success("Visualization generated!");
      }
    } catch (error) {
      console.error("Visualization error:", error);
      toast.error("Failed to generate visualization");
    } finally {
      setIsGeneratingViz(false);
    }
  };

  // Analysis function
  const runAnalysis = async () => {
    if (images.length === 0) {
      toast.error("Please upload at least one image");
      return;
    }

    stopSpeaking();
    setIsLoading(true);
    setAnalysisResults(null);
    setChatMessages([]);
    setShowChat(false);

    try {
      const analysisType = ANALYSIS_TYPES.find(t => t.id === activeAnalysis);
      const response = await axios.post(`${API}/admin/ai-tailor/analyze`, {
        analysis_type: activeAnalysis,
        images: images.map(img => ({
          base64: img.base64,
          type: img.type,
          name: img.name
        })),
        additional_notes: additionalNotes
      });

      const results = {
        type: activeAnalysis,
        label: analysisType.label,
        content: response.data.analysis,
        timestamp: new Date().toISOString(),
        imageCount: images.length
      };
      
      setAnalysisResults(results);
      setShowChat(true);
      
      // Generate new session for this analysis
      setSessionId(`chat_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
      
      toast.success("Analysis complete!");
      
      // Auto-speak the analysis
      await speakText(response.data.analysis);
      
    } catch (error) {
      console.error("Analysis error:", error);
      toast.error(error.response?.data?.detail || "Failed to analyze images");
    } finally {
      setIsLoading(false);
    }
  };

  const copyResults = () => {
    if (analysisResults) {
      navigator.clipboard.writeText(analysisResults.content);
      setCopied(true);
      toast.success("Copied to clipboard!");
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (!isAuthenticated) return null;

  return (
    <div className="min-h-screen pt-20 pb-12">
      {/* Header */}
      <section className="py-8 border-b border-white/5">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <button
              onClick={() => navigate("/admin")}
              className="flex items-center gap-2 text-muted-foreground hover:text-foreground mb-4 transition-colors"
              data-testid="back-to-dashboard-btn"
            >
              <ArrowLeft size={16} />
              Back to Dashboard
            </button>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                <Ruler size={24} className="text-white" />
              </div>
              <div>
                <h1 className="font-serif text-2xl sm:text-3xl tracking-tight">
                  AI Tailor
                </h1>
                <p className="text-sm text-muted-foreground">
                  AI-powered style, posture & garment analysis with voice
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-8">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left Panel - Analysis Type & Upload */}
            <div className="lg:col-span-1 space-y-6">
              {/* Analysis Type Selection */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                className="bg-card border border-white/10 rounded-lg p-6"
              >
                <h3 className="font-serif text-lg mb-4 flex items-center gap-2">
                  <Sparkles size={18} className="text-primary" />
                  Analysis Type
                </h3>
                <div className="space-y-3">
                  {ANALYSIS_TYPES.map((type) => (
                    <button
                      key={type.id}
                      onClick={() => {
                        setActiveAnalysis(type.id);
                        setAnalysisResults(null);
                        setChatMessages([]);
                      }}
                      data-testid={`analysis-type-${type.id}`}
                      className={`w-full p-4 rounded-lg border transition-all text-left ${
                        activeAnalysis === type.id
                          ? "border-primary bg-primary/10"
                          : "border-white/10 hover:border-white/20 hover:bg-white/5"
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${type.color} flex items-center justify-center`}>
                          <type.icon size={18} className="text-white" />
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-sm">{type.label}</p>
                          <p className="text-xs text-muted-foreground">{type.description}</p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </motion.div>

              {/* Voice Selection */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.05 }}
                className="bg-card border border-white/10 rounded-lg p-6"
              >
                <h3 className="font-serif text-lg mb-4 flex items-center gap-2">
                  <Volume2 size={18} className="text-primary" />
                  AI Voice
                </h3>
                <div className="grid grid-cols-2 gap-2">
                  {VOICE_OPTIONS.map((voice) => (
                    <button
                      key={voice.id}
                      onClick={() => setSelectedVoice(voice.id)}
                      data-testid={`voice-${voice.id}`}
                      className={`p-3 rounded-lg border text-left transition-all ${
                        selectedVoice === voice.id
                          ? "border-green-500 bg-green-500/10"
                          : "border-white/10 hover:border-white/20"
                      }`}
                    >
                      <p className="font-medium text-sm">{voice.label}</p>
                      <p className="text-xs text-muted-foreground">{voice.description}</p>
                    </button>
                  ))}
                </div>
              </motion.div>

              {/* Image Upload */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 }}
                className="bg-card border border-white/10 rounded-lg p-6"
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-serif text-lg flex items-center gap-2">
                    <ImageIcon size={18} className="text-primary" />
                    Images ({images.length})
                  </h3>
                  {images.length > 0 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={clearAllImages}
                      className="text-destructive hover:text-destructive"
                      data-testid="clear-all-images-btn"
                    >
                      <Trash2 size={14} className="mr-1" />
                      Clear All
                    </Button>
                  )}
                </div>

                {/* Camera Modal */}
                <AnimatePresence>
                  {isCameraOpen && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      className="mb-4"
                    >
                      <div className="relative bg-black rounded-lg overflow-hidden">
                        {cameraError ? (
                          <div className="p-4 text-center text-sm text-red-400">
                            <Camera size={32} className="mx-auto mb-2 opacity-50" />
                            {cameraError}
                          </div>
                        ) : (
                          <>
                            <video
                              ref={videoRef}
                              autoPlay
                              playsInline
                              muted
                              className="w-full aspect-video object-cover"
                            />
                            <canvas ref={canvasRef} className="hidden" />
                          </>
                        )}
                        <div className="absolute bottom-3 left-0 right-0 flex justify-center gap-2">
                          {!cameraError && (
                            <>
                              <Button
                                size="sm"
                                variant="secondary"
                                onClick={switchCamera}
                                className="h-8 w-8 p-0 rounded-full bg-white/20 hover:bg-white/30"
                              >
                                <SwitchCamera size={14} />
                              </Button>
                              <Button
                                size="sm"
                                onClick={capturePhoto}
                                className="h-10 w-10 p-0 rounded-full bg-white hover:bg-white/90"
                                data-testid="capture-photo-btn"
                              >
                                <div className="w-6 h-6 rounded-full border-2 border-gray-800" />
                              </Button>
                            </>
                          )}
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={closeCamera}
                            className="h-8 w-8 p-0 rounded-full"
                          >
                            <X size={14} />
                          </Button>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Upload Buttons */}
                <div className="flex gap-2 mb-4">
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleImageSelect}
                    accept="image/*"
                    multiple
                    className="hidden"
                  />
                  <Button
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    className="flex-1 border-white/10 hover:bg-purple-500/10 hover:border-purple-500/50"
                    disabled={isCameraOpen}
                    data-testid="upload-images-btn"
                  >
                    <Upload size={16} className="mr-2" />
                    Upload
                  </Button>
                  <Button
                    variant="outline"
                    onClick={openCamera}
                    className="flex-1 border-white/10 hover:bg-green-500/10 hover:border-green-500/50"
                    disabled={isCameraOpen}
                    data-testid="open-camera-btn"
                  >
                    <Camera size={16} className="mr-2" />
                    Camera
                  </Button>
                </div>

                {/* Image Preview Grid */}
                {images.length > 0 ? (
                  <div className="grid grid-cols-3 gap-2">
                    {images.map((img) => (
                      <div key={img.id} className="relative group aspect-square">
                        <img
                          src={img.preview}
                          alt={img.name}
                          className="w-full h-full object-cover rounded-lg"
                        />
                        <button
                          onClick={() => removeImage(img.id)}
                          className="absolute top-1 right-1 w-6 h-6 rounded-full bg-destructive text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                          data-testid={`remove-image-${img.id}`}
                        >
                          <X size={12} />
                        </button>
                      </div>
                    ))}
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="aspect-square rounded-lg border-2 border-dashed border-white/10 hover:border-white/30 flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
                      data-testid="add-more-images-btn"
                    >
                      <Plus size={24} />
                    </button>
                  </div>
                ) : (
                  <div className="border-2 border-dashed border-white/10 rounded-lg p-8 text-center">
                    <ImageIcon size={32} className="mx-auto text-muted-foreground mb-2" />
                    <p className="text-sm text-muted-foreground">
                      Upload multiple images for analysis
                    </p>
                  </div>
                )}

                {/* Additional Notes */}
                <div className="mt-4">
                  <label className="block text-sm text-muted-foreground mb-2">
                    Additional Notes (optional)
                  </label>
                  <Textarea
                    value={additionalNotes}
                    onChange={(e) => setAdditionalNotes(e.target.value)}
                    placeholder="Any specific areas to focus on..."
                    className="bg-background border-white/10 resize-none"
                    rows={2}
                    data-testid="additional-notes-input"
                  />
                </div>

                {/* Analyze Button */}
                <Button
                  onClick={runAnalysis}
                  disabled={images.length === 0 || isLoading}
                  className="w-full mt-4 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white"
                  data-testid="run-analysis-btn"
                >
                  {isLoading ? (
                    <>
                      <Loader2 size={16} className="mr-2 animate-spin" />
                      Analyzing...
                    </>
                  ) : (
                    <>
                      <Bot size={16} className="mr-2" />
                      Analyze {images.length} Image{images.length !== 1 ? 's' : ''}
                    </>
                  )}
                </Button>
              </motion.div>

              {/* Video Analysis Section */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.15 }}
                className="bg-card border border-white/10 rounded-lg p-6"
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-serif text-lg flex items-center gap-2">
                    <Video size={18} className="text-cyan-400" />
                    Video Analysis
                  </h3>
                  {video && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={clearVideo}
                      className="text-destructive hover:text-destructive"
                    >
                      <Trash2 size={14} className="mr-1" />
                      Clear
                    </Button>
                  )}
                </div>

                {/* Video Upload Area */}
                <input
                  type="file"
                  ref={videoInputRef}
                  onChange={handleVideoUpload}
                  accept="video/mp4,video/webm,video/quicktime,video/x-msvideo"
                  className="hidden"
                />
                
                {video ? (
                  <div className="space-y-4">
                    {/* Video Preview */}
                    <div className="relative rounded-lg overflow-hidden bg-black">
                      <video
                        src={videoPreview}
                        controls
                        className="w-full max-h-[200px] object-contain"
                      />
                      <div className="absolute top-2 right-2 px-2 py-1 bg-black/60 rounded text-xs">
                        {video.name}
                      </div>
                    </div>
                    
                    {/* Analysis Focus Selection */}
                    <div>
                      <label className="block text-sm text-muted-foreground mb-2">
                        Analysis Focus
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {VIDEO_FOCUS_OPTIONS.map(option => (
                          <button
                            key={option.id}
                            onClick={() => setVideoAnalysisFocus(option.id)}
                            className={`px-3 py-1.5 text-xs rounded-lg border transition-all ${
                              videoAnalysisFocus === option.id
                                ? "border-cyan-500 bg-cyan-500/10 text-cyan-400"
                                : "border-white/10 hover:border-white/30"
                            }`}
                          >
                            {option.label}
                          </button>
                        ))}
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        {VIDEO_FOCUS_OPTIONS.find(o => o.id === videoAnalysisFocus)?.description}
                      </p>
                    </div>
                    
                    {/* Analyze Video Button */}
                    <Button
                      onClick={analyzeVideo}
                      disabled={isAnalyzingVideo}
                      className="w-full bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white"
                    >
                      {isAnalyzingVideo ? (
                        <>
                          <Loader2 size={16} className="mr-2 animate-spin" />
                          Analyzing Video...
                        </>
                      ) : (
                        <>
                          <Film size={16} className="mr-2" />
                          Analyze Video
                        </>
                      )}
                    </Button>
                  </div>
                ) : (
                  <button
                    onClick={() => videoInputRef.current?.click()}
                    className="w-full border-2 border-dashed border-white/10 hover:border-cyan-500/50 rounded-lg p-8 text-center transition-colors group"
                  >
                    <Video size={32} className="mx-auto text-muted-foreground group-hover:text-cyan-400 mb-2 transition-colors" />
                    <p className="text-sm text-muted-foreground group-hover:text-foreground transition-colors">
                      Upload a video for AI analysis
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      MP4, WebM, MOV, AVI • Max 50MB
                    </p>
                  </button>
                )}

                {/* Video Analysis Results */}
                {videoAnalysisResult && (
                  <div className="mt-4 p-4 bg-gradient-to-br from-cyan-500/10 to-blue-500/10 border border-cyan-500/20 rounded-lg">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-medium text-cyan-400 flex items-center gap-2">
                        <Sparkles size={14} />
                        Video Summary
                      </h4>
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            navigator.clipboard.writeText(videoAnalysisResult);
                            toast.success("Copied to clipboard");
                          }}
                          className="h-7 px-2"
                        >
                          <Copy size={12} className="mr-1" />
                          Copy
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => speakText(videoAnalysisResult)}
                          disabled={isGeneratingAudio || isSpeaking}
                          className="h-7 px-2"
                        >
                          <Volume2 size={12} className="mr-1" />
                          {isSpeaking ? "Playing..." : "Listen"}
                        </Button>
                      </div>
                    </div>
                    <div className="prose prose-invert prose-sm max-w-none max-h-[400px] overflow-y-auto">
                      <div className="whitespace-pre-wrap text-sm">{videoAnalysisResult}</div>
                    </div>
                  </div>
                )}
              </motion.div>
            </div>

            {/* Right Panel - Results & Chat */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="lg:col-span-2 space-y-6"
            >
              {/* Analysis Results */}
              <div className="bg-card border border-white/10 rounded-lg p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="font-serif text-lg flex items-center gap-2">
                    <Eye size={18} className="text-primary" />
                    Analysis Results
                  </h3>
                  {analysisResults && (
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => isSpeaking ? stopSpeaking() : speakText(analysisResults.content)}
                        disabled={isGeneratingAudio}
                        className={`border-white/20 ${isSpeaking ? 'bg-green-500/20 border-green-500/50 text-green-400' : ''}`}
                        data-testid="read-aloud-btn"
                      >
                        {isGeneratingAudio ? (
                          <Loader2 size={14} className="mr-1 animate-spin" />
                        ) : isSpeaking ? (
                          <Square size={14} className="mr-1" />
                        ) : (
                          <Volume2 size={14} className="mr-1" />
                        )}
                        {isGeneratingAudio ? 'Loading...' : isSpeaking ? 'Stop' : 'Read Aloud'}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={copyResults}
                        className="border-white/20"
                        data-testid="copy-results-btn"
                      >
                        {copied ? <Check size={14} className="mr-1" /> : <Copy size={14} className="mr-1" />}
                        {copied ? 'Copied' : 'Copy'}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setShareContent(analysisResults.content);
                          setShareNote("");
                          setShareOpen(true);
                        }}
                        className="border-amber-500/30 text-amber-400 hover:bg-amber-500/10"
                        data-testid="ait-share-result-btn"
                      >
                        <Share2 size={14} className="mr-1" />
                        Share
                      </Button>
                    </div>
                  )}
                </div>

                {isLoading ? (
                  <div className="flex flex-col items-center justify-center h-64">
                    <div className="relative">
                      <div className="w-16 h-16 border-4 border-primary/20 rounded-full" />
                      <div className="absolute inset-0 w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin" />
                    </div>
                    <p className="mt-4 text-muted-foreground">Analyzing with AI...</p>
                  </div>
                ) : analysisResults ? (
                  <div className="space-y-4">
                    <div className="bg-gradient-to-r from-primary/10 to-purple-500/10 border border-primary/20 rounded-lg p-4">
                      <div className="flex items-center gap-3">
                        {ANALYSIS_TYPES.find(t => t.id === analysisResults.type)?.icon && (
                          <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${ANALYSIS_TYPES.find(t => t.id === analysisResults.type)?.color} flex items-center justify-center`}>
                            {(() => {
                              const Icon = ANALYSIS_TYPES.find(t => t.id === analysisResults.type)?.icon;
                              return Icon ? <Icon size={18} className="text-white" /> : null;
                            })()}
                          </div>
                        )}
                        <div>
                          <p className="font-medium">{analysisResults.label}</p>
                          <p className="text-xs text-muted-foreground">
                            Analyzed {analysisResults.imageCount} image{analysisResults.imageCount !== 1 ? 's' : ''}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="bg-background border border-white/5 rounded-lg p-6 whitespace-pre-wrap text-sm leading-relaxed max-h-64 overflow-y-auto" data-testid="analysis-results-content">
                      {analysisResults.content}
                    </div>
                    
                    {/* Visualize Adjustments Button - Only for garment analysis */}
                    {analysisResults.type === 'garment' && (
                      <div className="pt-4 border-t border-white/5">
                        <Button
                          onClick={generateVisualization}
                          disabled={isGeneratingViz}
                          className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white"
                          data-testid="visualize-adjustments-btn"
                        >
                          {isGeneratingViz ? (
                            <>
                              <Loader2 size={16} className="mr-2 animate-spin" />
                              Generating Visualization...
                            </>
                          ) : (
                            <>
                              <Wand2 size={16} className="mr-2" />
                              Visualize Adjustments
                            </>
                          )}
                        </Button>
                        <p className="text-xs text-muted-foreground text-center mt-2">
                          Generate an AI image showing the garment after recommended adjustments
                        </p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center h-64 text-center">
                    <Bot size={32} className="text-muted-foreground mb-4" />
                    <h4 className="font-medium mb-2">Ready to Analyze</h4>
                    <p className="text-sm text-muted-foreground max-w-sm">
                      Upload images and click Analyze to get AI-powered insights
                    </p>
                  </div>
                )}
              </div>

              {/* Conversation Chat */}
              {showChat && analysisResults && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-card border border-white/10 rounded-lg p-6"
                >
                  <h3 className="font-serif text-lg mb-4 flex items-center gap-2">
                    <MessageCircle size={18} className="text-primary" />
                    Continue Conversation
                  </h3>
                  
                  {/* Chat Messages */}
                  <div className="bg-background border border-white/5 rounded-lg p-4 h-64 overflow-y-auto mb-4">
                    {chatMessages.length === 0 ? (
                      <div className="h-full flex items-center justify-center text-muted-foreground text-sm">
                        Ask follow-up questions about the analysis...
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {chatMessages.map((msg, idx) => (
                          <div
                            key={idx}
                            className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                          >
                            <div
                              className={`max-w-[80%] rounded-lg p-3 ${
                                msg.role === 'user'
                                  ? 'bg-primary text-primary-foreground'
                                  : 'bg-white/5 border border-white/10'
                              }`}
                            >
                              <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                              {msg.role === 'assistant' && (
                                <div className="mt-2 flex items-center gap-3">
                                  <button
                                    onClick={() => speakText(msg.content)}
                                    disabled={isGeneratingAudio || isSpeaking}
                                    className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
                                  >
                                    <Volume2 size={12} />
                                    Play
                                  </button>
                                  <button
                                    onClick={() => {
                                      setShareContent(msg.content);
                                      setShareNote("");
                                      setShareOpen(true);
                                    }}
                                    className="text-xs text-muted-foreground hover:text-amber-400 flex items-center gap-1"
                                    data-testid="ait-share-btn"
                                  >
                                    <Share2 size={12} />
                                    Share to Messenger
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                        {isChatLoading && (
                          <div className="flex justify-start">
                            <div className="bg-white/5 border border-white/10 rounded-lg p-3">
                              <Loader2 size={16} className="animate-spin" />
                            </div>
                          </div>
                        )}
                        <div ref={chatEndRef} />
                      </div>
                    )}
                  </div>
                  
                  {/* Chat Input */}
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={toggleRecording}
                      disabled={isTranscribing}
                      className={`shrink-0 ${isRecording ? 'bg-red-500/20 border-red-500 text-red-400 animate-pulse' : 'border-white/10'}`}
                      data-testid="voice-input-btn"
                    >
                      {isTranscribing ? (
                        <Loader2 size={18} className="animate-spin" />
                      ) : isRecording ? (
                        <MicOff size={18} />
                      ) : (
                        <Mic size={18} />
                      )}
                    </Button>
                    <Input
                      value={chatInput}
                      onChange={(e) => setChatInput(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && sendChatMessage()}
                      placeholder="Ask a follow-up question..."
                      className="bg-background border-white/10"
                      disabled={isChatLoading}
                      data-testid="chat-input"
                    />
                    <Button
                      onClick={() => sendChatMessage()}
                      disabled={!chatInput.trim() || isChatLoading}
                      className="shrink-0 bg-primary hover:bg-primary/90"
                      data-testid="send-message-btn"
                    >
                      <Send size={18} />
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2 text-center">
                    Click the mic to speak, or type your question
                  </p>
                </motion.div>
              )}

              {/* Visualization Result */}
              {showVisualization && visualizationImage && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-card border border-amber-500/30 rounded-lg p-6"
                >
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-serif text-lg flex items-center gap-2">
                      <Wand2 size={18} className="text-amber-400" />
                      Adjustment Visualization
                    </h3>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setShowVisualization(false);
                        setVisualizationImage(null);
                      }}
                      className="text-muted-foreground hover:text-foreground"
                    >
                      <X size={16} />
                    </Button>
                  </div>
                  
                  <div className="space-y-4">
                    {/* Generated Image */}
                    <div className="relative rounded-lg overflow-hidden bg-black/20 border border-white/10">
                      <img
                        src={`data:image/png;base64,${visualizationImage.base64}`}
                        alt="Adjustment visualization"
                        className="w-full h-auto max-h-[500px] object-contain"
                        data-testid="visualization-image"
                      />
                      <button
                        onClick={() => {
                          const link = document.createElement('a');
                          link.href = `data:image/png;base64,${visualizationImage.base64}`;
                          link.download = `adjustment_visualization_${Date.now()}.png`;
                          link.click();
                        }}
                        className="absolute top-2 right-2 p-2 bg-black/50 rounded-lg hover:bg-black/70 transition-colors"
                        title="Download image"
                      >
                        <ZoomIn size={16} className="text-white" />
                      </button>
                    </div>
                    
                    {/* Description */}
                    {visualizationImage.summary && (
                      <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4">
                        <p className="text-sm text-muted-foreground">
                          <span className="font-medium text-amber-400">AI Vision:</span>{' '}
                          {visualizationImage.summary}
                        </p>
                      </div>
                    )}
                    
                    {/* Regenerate Button */}
                    <Button
                      variant="outline"
                      onClick={generateVisualization}
                      disabled={isGeneratingViz}
                      className="w-full border-amber-500/30 hover:bg-amber-500/10"
                      data-testid="regenerate-viz-btn"
                    >
                      {isGeneratingViz ? (
                        <>
                          <Loader2 size={16} className="mr-2 animate-spin" />
                          Regenerating...
                        </>
                      ) : (
                        <>
                          <Wand2 size={16} className="mr-2" />
                          Regenerate Visualization
                        </>
                      )}
                    </Button>
                  </div>
                </motion.div>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      <AITailorShareDialog
        open={shareOpen}
        onOpenChange={setShareOpen}
        content={shareContent}
        note={shareNote}
        setNote={setShareNote}
      />
    </div>
  );
};

// Share dialog for forwarding analysis / chat answers to the internal messenger
const AITailorShareDialog = ({ open, onOpenChange, content, note, setNote }) => {
  const [channels, setChannels] = useState([]);
  const [users, setUsers] = useState([]);
  const [target, setTarget] = useState(null);
  const [busy, setBusy] = useState(false);
  const me = (() => {
    try { return JSON.parse(sessionStorage.getItem("adminUser") || "null"); } catch { return null; }
  })();

  useEffect(() => {
    if (!open || !me) return;
    setTarget(null);
    Promise.all([
      axios.get(`${API}/chat/channels`).then(r => r.data?.channels || []).catch(() => []),
      axios.get(`${API}/chat/users`).then(r => (r.data?.users || []).filter(u => u.id !== me.id)).catch(() => []),
    ]).then(([c, u]) => { setChannels(c); setUsers(u); });
  }, [open, me?.id]);

  const send = async () => {
    if (!target || !me) return;
    try {
      setBusy(true);
      await axios.post(`${API}/chat/share`, {
        conversation_id: target.id,
        sender_user_id: me.id,
        text: note || "",
        shared_content: content,
        shared_label: "AI Tailor",
      });
      toast.success(`Shared to ${target.label}`);
      onOpenChange(false);
    } catch {
      toast.error("Failed to share");
    } finally {
      setBusy(false);
    }
  };

  const dmId = (a, b) => {
    const [x, y] = [a, b].sort();
    return `dm:${x}:${y}`;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-sm">
            <Share2 size={14} className="text-amber-400" />
            Share to Messenger
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="bg-muted/30 rounded-lg p-2 text-xs text-muted-foreground max-h-32 overflow-y-auto whitespace-pre-wrap">
            {content}
          </div>
          <Textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Add a note (optional)…"
            rows={2}
            className="text-xs"
            data-testid="ait-share-note"
          />
          <div>
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Channels</p>
            <div className="flex flex-wrap gap-1.5">
              {channels.map((c) => (
                <button
                  key={c.id}
                  onClick={() => setTarget({ id: c.id, label: `#${c.name}` })}
                  className={`text-xs px-2 py-1 rounded flex items-center gap-1 transition-colors ${
                    target?.id === c.id ? "bg-amber-500/20 text-amber-400 ring-1 ring-amber-500/30" : "bg-muted/40 hover:bg-muted/60"
                  }`}
                  data-testid={`ait-share-channel-${c.name}`}
                >
                  <Hash size={10} /> {c.name}
                </button>
              ))}
            </div>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Direct Messages</p>
            <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto">
              {users.map((u) => {
                const tid = dmId(me?.id || "", u.id);
                return (
                  <button
                    key={u.id}
                    onClick={() => setTarget({ id: tid, label: u.full_name || u.username })}
                    className={`text-xs px-2 py-1 rounded flex items-center gap-1 transition-colors ${
                      target?.id === tid ? "bg-amber-500/20 text-amber-400 ring-1 ring-amber-500/30" : "bg-muted/40 hover:bg-muted/60"
                    }`}
                    data-testid={`ait-share-dm-${u.username}`}
                  >
                    <Users size={10} /> {u.full_name || u.username}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)} disabled={busy}>
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={send}
            disabled={!target || busy}
            className="bg-gradient-to-br from-amber-500 to-purple-600 text-white"
            data-testid="ait-share-send-btn"
          >
            {busy ? <Loader2 size={12} className="animate-spin mr-1" /> : <Send size={12} className="mr-1" />}
            Share to {target?.label || "…"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AdminAITailorPage;
